import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { useState } from 'react';
import { Row } from '@tanstack/react-table';
import { useAuthUser } from '../../hooks/useAuthUser';
import { useMutation } from 'react-query';
import { cancelInvitation, inviteUser } from 'api/users/api';
import {
  InvitationItem,
  InviteUserRequest,
  InviteUserResponse,
} from '../../api/users/schemas/Invitations';
import showToast from '../../components/Toast/Toast';
import { BEUserRole } from '../../api/users/schemas/UserRole';
import { useInviteTableRefetch } from './useInviteTableRefetch';

export const InvitationActions = ({ row }: { row: Row<InvitationItem> }) => {
  const [isSubmiting, setIsSubmiting] = useState<boolean>(false);

  const { name: inviterName } = useAuthUser();
  const refetchInviteList = useInviteTableRefetch();

  const cancelInvitationMutation = useMutation(cancelInvitation, {
    onSuccess: (response) => {
      if (!isSubmiting) {
        if (response) {
          refetchInviteList();
          showToast.confirmation({ message: 'Invitation revoked.' });
        } else {
          showToast.error({ message: 'Unknown error revoking invitation.' });
        }
      }
    },
  });

  const cancelInvitationMutationNoToastNotification =
    useMutation(cancelInvitation);

  const resendInvitationMutation = useMutation(
    (request: InviteUserRequest) => {
      return inviteUser(request);
    },
    {
      onSuccess: (response: InviteUserResponse[]) => {
        if (response.length !== 1) {
          showToast.error({ message: 'An unknown error occured' });
        }
        const status = response[0];
        if (status.result === 'Success') {
          showToast.confirmation({ message: 'Invite sent' });
        } else {
          showToast.error({ message: status.error_message });
        }
      },
      onSettled: () => {
        refetchInviteList();
        setIsSubmiting(false);
      },
    },
  );

  return (
    <div className="flex items-center justify-between gap-4 pr-2">
      <GraniteButton
        disabled={isSubmiting}
        variant="secondary"
        size="small"
        onClick={() => {
          setIsSubmiting(true);
          cancelInvitationMutationNoToastNotification.mutate(row.original.id);
          resendInvitationMutation.mutate([
            {
              first_name: row.original.first_name,
              last_name: row.original.last_name,
              email: row.original.email,
              phone_number: row.original.phone_number ?? '',
              role: row.original.role as BEUserRole,
              inviter_name: inviterName,
            },
          ]);
        }}
      >
        Resend
      </GraniteButton>
      <GraniteButton
        variant="destructive"
        disabled={isSubmiting}
        size="small"
        onClick={() => {
          setIsSubmiting(true);
          cancelInvitationMutation.mutate(row.original.id);
        }}
      >
        Revoke
      </GraniteButton>
    </div>
  );
};
